import { useState, useEffect, useRef } from "react";
import apiClient from "../business/network/ApiClient";

const DEBOUNCE_MS = 300;

/**
 * Fetches @mention suggestions whenever `mentionQuery` changes.
 *
 * @param {string|null} mentionQuery  The text after '@', or null when @mention mode is inactive.
 * @param {number|null} ouId          The user's current OU id.
 * @returns {{ suggestions: Array, isLoading: boolean }}
 */
export function useMentionCompletion(mentionQuery, ouId) {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading]     = useState(false);
  const debounceRef                   = useRef(null);
  const abortRef                      = useRef(null);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    abortRef.current?.abort();

    // Require at least 2 non-space characters so "@j " doesn't fire a request.
    if (!mentionQuery || mentionQuery.trim().length < 2 || !ouId) {
      setSuggestions([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    debounceRef.current = setTimeout(async () => {
      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const results = await apiClient.mentionSuggest(mentionQuery, ouId, controller.signal);
        setSuggestions(results);
      } catch {
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      clearTimeout(debounceRef.current);
      abortRef.current?.abort();
    };
  }, [mentionQuery, ouId]);

  return { suggestions, isLoading };
}
